/**
 * Repository operations — clone, pull, scan, resolve, link.
 *
 * Remote repos are cloned to a shared global cache (~/.skaile/cache/sources/<name>/).
 * Projects reference the cache via symlinks at .skaile/cache/sources/<name>/.
 * Local repos (path:) are used directly. Linked repos override remote URLs.
 *
 * Supports partial clone + sparse checkout for large repos.
 */

import { spawnSync } from "node:child_process";
import {
  existsSync,
  lstatSync,
  mkdirSync,
  readFileSync,
  rmSync,
  symlinkSync,
  writeFileSync,
} from "node:fs";
import { homedir } from "node:os";
import { dirname, join, resolve, sep } from "node:path";
import semver from "semver";
import { parse, stringify } from "yaml";
import { globalCacheRoot, mergeLegacyEntriesInto, mergeLegacyEntriesKeyed } from "./cache-paths.js";
import { scanDirectory } from "./manifest.js";
import type { AssetRef, CatalogEntry } from "./models.js";
import { assetRefToStr, parseAssetRef } from "./models.js";
import { SKAILE_MANIFEST_FILENAME } from "./publish-manifest.js";
import { normalizeGithubOrg, type ProvenanceCandidate, type ProvenanceIndex } from "./walker.js";
import type { OverrideEntry, SourceDeclaration, StoreEntry } from "./workspace-config.js";

// ── Global clone cache ───────────────────────────────────────────────────────

/** True if `dir` is itself a git repo (has its own `.git`). */
function hasOwnGit(dir: string): boolean {
  return existsSync(join(dir, ".git"));
}

/**
 * The global source-clone cache: `<cacheRoot>/sources` (override the root with
 * `SKAILE_CACHE_DIR`). On first call it migrates both legacy clone roots into it:
 * the whole of `~/.skaile/repos/*`, and the own-`.git` (clone) entries of
 * `~/.skaile/sources/*`. The non-clone remainder of `~/.skaile/sources` is the
 * sidecar repo and is migrated separately by `getSidecarRoot()`.
 * @docLink packages/core/api-reference#get-global-cache-dir
 */
export function getGlobalCacheDir(): string {
  const dir = join(globalCacheRoot(), "sources");
  // Legacy roots are anchored at SKAILE_HOME, not the cache root — do not derive
  // them from globalCacheRoot() (SKAILE_CACHE_DIR may point elsewhere).
  const home = process.env.SKAILE_HOME ?? join(homedir(), ".skaile");
  mergeLegacyEntriesInto(dir, join(home, "repos"), () => true);
  // Legacy `~/.skaile/sources/<bare-name>` clones were keyed by the bare repo
  // name, so a URL-keyed lookup (`<host>/<owner>/<repo>`) missed them and they
  // lingered mis-placed. Re-derive each clone's true key from its `origin`
  // remote and land it at the URL-keyed path; a clone with no readable origin
  // (or a non-GitHub URL) falls back to its bare name.
  mergeLegacyEntriesKeyed(dir, join(home, "sources"), hasOwnGit, (from) => {
    const origin = getRepoRemoteUrl(from);
    return origin ? sourceCacheKey(origin) : null;
  });
  return dir;
}

// ── Clone-cache key ──────────────────────────────────────────────────────────

/**
 * Canonical clone-cache key for a source git URL: `<host>/<owner>/<repo>`
 * (lowercased host; `.git` suffix, trailing slash, and protocol/scp prefix
 * stripped). This is the SSOT for the clone-cache path under
 * `getGlobalCacheDir()` — every writer and reader of that cache MUST derive its
 * key here.
 *
 * It exists because the old "last path segment" slug collided: both
 * `github.com/orgA/ai-assets` and `github.com/orgB/ai-assets` mapped to
 * `ai-assets`, so the wrong repo was served from cache silently. Keying by the
 * full `<host>/<owner>/<repo>` triple makes that collision impossible.
 *
 * Non-GitHub or unparseable URLs (local paths, `file://`, exotic hosts) fall
 * back to the last path segment so nothing throws.
 *
 * Userinfo is dropped, so `https://x-access-token:<token>@host/o/r` keys the
 * same clone as the plain url — and the token never becomes a directory name
 * or leaks through a message that names the key (`install` reports failures as
 * `source:<key> (…)`).
 *
 * @docLink packages/core/api-reference#source-cache-key
 */
export function sourceCacheKey(url: string): string {
  const trimmed = url.trim();
  const normalized = trimmed
    .replace(/^git@([^:]+):/, "$1/") // git@host:owner/repo → host/owner/repo
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, "") // drop scheme (https://, ssh://, file://, …)
    .replace(/^[^/]*@/, "") // drop userinfo (user:token@) — never key on a secret
    .replace(/\.git$/i, "")
    .replace(/\/+$/, "");
  const m = /^([^/]+)\/([^/]+)\/([^/]+)$/.exec(normalized);
  if (m) {
    const [, host, owner, repo] = m;
    return `${host.toLowerCase()}/${owner}/${repo}`;
  }
  // Fall back from the NORMALIZED url, not the raw one — else the userinfo
  // strip above is skipped on this path and a token can become the key.
  return lastSegment(normalized);
}

function lastSegment(url: string): string {
  return (
    url
      .replace(/\.git$/i, "")
      .replace(/\/+$/, "")
      .split(/[/:]/)
      .pop() || "source"
  );
}

/**
 * Short repo name (`<repo>`) for a cache key produced by {@link sourceCacheKey}
 * (or a raw URL). Used where a single human-facing label or library name is
 * wanted instead of the full `<host>/<owner>/<repo>` triple.
 */
export function sourceShortName(cacheKeyOrUrl: string): string {
  return sourceCacheKey(cacheKeyOrUrl).replace(/\/+$/, "").split("/").pop() || "source";
}

/**
 * Collision-free, filesystem-safe flat slug for a source URL — the basename of
 * its curated store manifest (`~/.skaile/store/manifests/<slug>.yaml`). Derived
 * from the full {@link sourceCacheKey} (`<host>/<owner>/<repo>`) with path
 * separators encoded, so `orgA/cli` and `orgB/cli` never collide on the trailing
 * repo name (which {@link sourceShortName} would). Keyed identically by the
 * `init` writer, the `source add`/`sync` overlay reader, and the resolver.
 */
export function storeManifestSlug(url: string): string {
  return sourceCacheKey(url).replace(/[/\\:]+/g, "__");
}

// ── Git operations ───────────────────────────────────────────────────────────

/**
 * Shared env for all git subprocess spawns.
 * GIT_TERMINAL_PROMPT=0 prevents git from opening /dev/tty to ask for
 * credentials when running inside a CLI spinner (which holds the terminal in
 * raw mode). Without this, git bypasses stdio: "pipe" and the process hangs
 * indefinitely waiting for input that can never arrive.
 */
const GIT_ENV = { ...process.env, GIT_TERMINAL_PROMPT: "0" };

/**
 * Clone a remote git repository using partial clone (treeless) with sparse checkout.
 * Falls back to a regular shallow clone if the server does not support partial clones.
 *
 * @param url - Remote git URL
 * @param branch - Branch to clone
 * @param dest - Local destination directory
 * @returns `true` on success, `false` on failure
 * @docLink packages/core/api-reference#clone-repo
 */
export function cloneRepo(url: string, branch: string, dest: string): boolean {
  mkdirSync(dest, { recursive: true });
  // Try partial clone first (requires git 2.25+ and server support)
  const r = spawnSync(
    "git",
    ["clone", "--depth=1", "--filter=blob:none", "--sparse", "--branch", branch, url, dest],
    { stdio: "pipe", env: GIT_ENV },
  );
  if (r.status === 0) {
    // Enable cone mode for sparse checkout
    spawnSync("git", ["-C", dest, "sparse-checkout", "init", "--cone"], {
      stdio: "pipe",
      env: GIT_ENV,
    });
    // Check out everything initially (for discovery)
    spawnSync("git", ["-C", dest, "sparse-checkout", "disable"], { stdio: "pipe", env: GIT_ENV });
    return true;
  }
  // Fallback: regular shallow clone
  const fallback = spawnSync("git", ["clone", "--depth=1", "--branch", branch, url, dest], {
    stdio: "pipe",
    env: GIT_ENV,
  });
  return fallback.status === 0;
}

/**
 * Blank out every `userinfo@host` in a message (`https://user:token@host/…` and
 * the scheme-less `user:token@host/…` that {@link sourceCacheKey} bakes into a
 * cache path). Git echoes the configured remote verbatim in transport errors,
 * and a credentialed source url also keys its clone directory, so the git
 * message and the path both carry the token. Redacting a secret-free `git@host`
 * too costs readability; missing one token costs a credential.
 */
function redactCredentials(text: string): string {
  return text.replace(/[^/\s@]+@/g, "***@");
}

/** Readable one-line reason for a failed git spawn (stderr, else exit status). */
function gitFailure(r: ReturnType<typeof spawnSync>): string {
  const stderr = r.stderr ? String(r.stderr).trim() : "";
  if (stderr) return redactCredentials(stderr.split("\n").slice(-2).join(" ").trim());
  if (r.error) return redactCredentials(r.error.message);
  return `git exited with status ${r.status}`;
}

/** Result of {@link pullRepoResult}: success flag plus git's reason on failure. */
export interface PullResult {
  ok: boolean;
  error?: string;
}

/**
 * Pull the latest commits for a cloned repository, reporting git's own failure
 * reason. Same strategy as {@link pullRepo} — of which this is the detailed
 * form — so callers can surface *why* a refresh failed instead of swallowing it.
 *
 * @param dest - Local repo directory
 * @param branch - Branch to pull
 * @returns `{ ok: true }`, or `{ ok: false, error }` carrying the git message
 * @docLink packages/core/api-reference#pull-repo-result
 */
export function pullRepoResult(dest: string, branch: string): PullResult {
  const pull = spawnSync("git", ["-C", dest, "pull", "--depth=1", "--ff-only"], {
    stdio: "pipe",
    env: GIT_ENV,
  });
  if (pull.status === 0) return { ok: true };
  // Shallow clones can diverge — force-sync via fetch + reset
  const fetch = spawnSync("git", ["-C", dest, "fetch", "--depth=1", "origin", branch], {
    stdio: "pipe",
    env: GIT_ENV,
  });
  if (fetch.status !== 0) return { ok: false, error: gitFailure(fetch) };
  const reset = spawnSync("git", ["-C", dest, "reset", "--hard", `origin/${branch}`], {
    stdio: "pipe",
    env: GIT_ENV,
  });
  return reset.status === 0 ? { ok: true } : { ok: false, error: gitFailure(reset) };
}

/**
 * Pull the latest commits for a cloned repository.
 * On shallow clone divergence, falls back to `git fetch --depth=1` + `git reset --hard`.
 *
 * @param dest - Local repo directory
 * @param branch - Branch to pull
 * @returns `true` on success, `false` on failure
 * @docLink packages/core/api-reference#pull-repo
 */
export function pullRepo(dest: string, branch: string): boolean {
  return pullRepoResult(dest, branch).ok;
}

/**
 * Return the current HEAD commit SHA for a local repository.
 *
 * @param repoDir - Absolute path to the cloned repository
 * @returns Full commit SHA string, or `null` on failure
 * @docLink packages/core/api-reference#get-repo-commit
 */
export function getRepoCommit(repoDir: string): string | null {
  const r = spawnSync("git", ["-C", repoDir, "rev-parse", "HEAD"], {
    encoding: "utf8",
    stdio: "pipe",
  });
  return r.status === 0 ? r.stdout.trim() : null;
}

/**
 * Check out a specific tag, branch, or commit SHA in a local repository.
 * Fetches the ref from `origin` first, then attempts `FETCH_HEAD` checkout.
 *
 * @param repoDir - Absolute path to the cloned repository
 * @param pin - Tag, branch name, or commit SHA
 * @returns `true` on success, `false` on failure
 * @docLink packages/core/api-reference#checkout-pin
 */
export function checkoutPin(repoDir: string, pin: string): boolean {
  // Fetch the ref first (works for tags, branches, and commits)
  spawnSync("git", ["-C", repoDir, "fetch", "--depth=1", "origin", pin], {
    stdio: "pipe",
    env: GIT_ENV,
  });
  const r = spawnSync("git", ["-C", repoDir, "checkout", "FETCH_HEAD"], {
    stdio: "pipe",
    env: GIT_ENV,
  });
  if (r.status === 0) return true;
  // Fallback: try direct checkout (for branches already fetched)
  const r2 = spawnSync("git", ["-C", repoDir, "checkout", pin], { stdio: "pipe", env: GIT_ENV });
  return r2.status === 0;
}

/** Read a clone's `origin` remote URL, or `null` when it is absent / not a repo. */
function getRepoRemoteUrl(repoDir: string): string | null {
  const r = spawnSync("git", ["-C", repoDir, "remote", "get-url", "origin"], {
    encoding: "utf8",
    stdio: "pipe",
    env: GIT_ENV,
  });
  return r.status === 0 ? r.stdout.trim() : null;
}

/** True when the clone's origin maps to the same cache key as the declared URL. */
function remoteUrlMatches(repoDir: string, expectedUrl: string): boolean {
  // Only a dir that is its OWN git root can be verified. A local source path that
  // is NOT a standalone clone (e.g. the in-repo factory-assets tree) would make
  // `git -C` report the ENCLOSING repo's remote, falsely "mismatch", and get
  // wiped. Treat "not its own clone" as a match so it is never destroyed.
  if (!hasOwnGit(repoDir)) return true;
  const actual = getRepoRemoteUrl(repoDir);
  if (!actual) return true; // no remote info → don't destroy; assume ok
  return sourceCacheKey(actual) === sourceCacheKey(expectedUrl);
}

// ── Link management ──────────────────────────────────────────────────────────

/**
 * Map of repo name → local filesystem path for development overrides.
 * Stored in `.skaile/links.yaml`; used to redirect a named repo to a local clone.
 * @docLink packages/core/api-reference#link-config
 */
export interface LinkConfig {
  [repoName: string]: string; // repo name → local path
}

/** Path to the project-local links file. */
function linksFile(projectDir: string): string {
  return join(projectDir, ".skaile", "links.yaml");
}

/**
 * Read the dev-link map from `.skaile/links.yaml` in the project directory.
 * Returns an empty object when no links file exists.
 *
 * @param projectDir - Absolute path to the project root
 * @returns `LinkConfig` map (may be empty)
 * @docLink packages/core/api-reference#read-links
 */
export function readLinks(projectDir: string): LinkConfig {
  const file = linksFile(projectDir);
  if (!existsSync(file)) return {};
  try {
    return (parse(readFileSync(file, "utf8")) as LinkConfig) ?? {};
  } catch {
    return {};
  }
}

/**
 * Persist the dev-link map to `.skaile/links.yaml` in the project directory.
 *
 * @param projectDir - Absolute path to the project root
 * @param links - Updated `LinkConfig` to write
 * @docLink packages/core/api-reference#write-links
 */
export function writeLinks(projectDir: string, links: LinkConfig): void {
  const dir = join(projectDir, ".skaile");
  mkdirSync(dir, { recursive: true });
  writeFileSync(linksFile(projectDir), stringify(links));
}

/**
 * Register a local filesystem path as a dev override for a named repository.
 * The path is stored in `.skaile/links.yaml` and takes priority over the remote URL at runtime.
 *
 * @param projectDir - Absolute path to the project root
 * @param repoName - Name of the repository to override
 * @param localPath - Local path (absolute or relative to `projectDir`) to use instead
 * @throws When the resolved path does not exist
 * @docLink packages/core/api-reference#link-repo
 */
export function linkRepo(projectDir: string, repoName: string, localPath: string): void {
  const resolved = resolve(projectDir, localPath);
  if (!existsSync(resolved)) {
    throw new Error(`Path not found: ${localPath} (resolved: ${resolved})`);
  }
  const links = readLinks(projectDir);
  links[repoName] = resolved;
  writeLinks(projectDir, links);
}

/**
 * Remove the dev-link override for a named repository, reverting it to the remote URL.
 *
 * @param projectDir - Absolute path to the project root
 * @param repoName - Name of the repository to unlink
 * @returns `true` if the link existed and was removed, `false` if no link was found
 * @docLink packages/core/api-reference#unlink-repo
 */
export function unlinkRepo(projectDir: string, repoName: string): boolean {
  const links = readLinks(projectDir);
  if (!links[repoName]) return false;
  delete links[repoName];
  writeLinks(projectDir, links);
  return true;
}

// ── Repo lifecycle ───────────────────────────────────────────────────────────

/**
 * Ensure a repository is available locally, cloning or symlinking as needed.
 *
 * Resolution order:
 * 1. Linked override (`.skaile/links.yaml`) — highest priority
 * 2. Local path (`decl.path`) — direct filesystem reference; a bare path must
 *    already exist, but a url-backed managed cache dir is cloned in-place on miss
 * 3. Remote URL (`decl.url`) — cloned to the shared global cache, symlinked into `.skaile/cache/sources/`
 *
 * Freshness: a **managed cache clone** (a url-backed dir under
 * {@link getGlobalCacheDir}) is refreshed on every call when no pin is given —
 * both for a bare `url:` declaration and for the `path:`-into-the-cache form
 * that `AssetManager` builds from `sources:`, so `install` never serves a stale
 * source. A failed refresh warns and keeps the cached commit. A user-owned
 * checkout (bare `path:`, dev link, factory tree) is never touched.
 *
 * Pin support: when `opts.pin` is provided, the tag/commit is checked out and
 * the clone is never pulled.
 *
 * @param decl - Repository declaration from `skaile.yaml`
 * @param name - Logical repository name (cache key)
 * @param reposDir - Project-local source-clone directory (`.skaile/cache/sources/`)
 * @param opts - Optional: `pin` for a specific commit/tag, `projectDir` for link lookup
 * @returns Absolute path to the local repository directory
 * @throws When the repository cannot be resolved or cloned
 * @docLink packages/core/api-reference#ensure-repo
 */
export function ensureRepo(
  decl: SourceDeclaration,
  name: string,
  reposDir: string,
  opts?: { pin?: string; projectDir?: string },
): string {
  // 1. Check for linked override
  const linked = resolveLinkedOverride(name, opts?.projectDir);
  if (linked) return linked;

  // 2. Local path (or managed cache dir for a remote source)
  if (decl.path) {
    return ensureLocalPathRepo(decl, reposDir, opts);
  }

  // 3. Remote URL — clone to shared global cache
  if (decl.url) {
    return ensureRemoteRepo(decl, name, reposDir, opts);
  }

  throw new Error(`Repository "${name}" has neither url nor path`);
}

/**
 * Step 1 of {@link ensureRepo}: resolve a dev-link override for `name`, if one
 * exists. Returns the linked path (validated to exist) or `null` when no link
 * is configured.
 */
function resolveLinkedOverride(name: string, projectDir?: string): string | null {
  if (!projectDir) return null;
  const links = readLinks(projectDir);
  const linked = links[name];
  if (!linked) return null;
  if (!existsSync(linked)) {
    throw new Error(
      `Linked path not found: ${linked}. Remove the .skaile/links.yaml entry to clear it.`,
    );
  }
  return linked;
}

/**
 * True when `dir` lives inside the global clone cache — i.e. it is a clone
 * skaile itself created and may therefore refresh. A user's own checkout
 * (a bare `path:` source, a dev link, the in-package factory tree) never is,
 * and must never be pulled or reset under them.
 */
function isManagedCacheDir(dir: string): boolean {
  const root = resolve(getGlobalCacheDir());
  const target = resolve(dir);
  return target === root || target.startsWith(root + sep);
}

/**
 * THE one place that brings a managed cache clone to the commit it should be on.
 * Clone on miss (or on a stale flat-slug collision), pull on an unpinned hit,
 * check out the pin when one is given. Both `ensureRepo` routes — a `path:`
 * declaration pointing into the cache and a bare `url:` one — go through here,
 * so "refresh a managed clone" has a single definition.
 *
 * A failed refresh is warned, not thrown: an unreachable network must not fail
 * an install that the existing clone can still serve.
 */
function syncManagedClone(dest: string, url: string, branch: string, pin?: string): void {
  const cached = existsSync(join(dest, ".git"));
  if (cached && remoteUrlMatches(dest, url)) {
    // The declaration is authoritative for credentials: the key ignores
    // userinfo, so a clone made with a since-rotated token still matches and
    // would keep fetching with the revoked one.
    spawnSync("git", ["-C", dest, "remote", "set-url", "origin", url], {
      stdio: "pipe",
      env: GIT_ENV,
    });
    // An unpinned source tracks its branch — refresh on every call, else the
    // clone is served forever at whatever commit it was first cloned at.
    if (!pin) {
      const { ok, error } = pullRepoResult(dest, branch);
      if (!ok) {
        // `dest` is a cache path keyed off the url, so it carries the token too
        // when the source url does — redact the assembled line, not just `error`.
        console.warn(
          redactCredentials(
            `[repo-manager] could not refresh source clone ${dest} (branch: ${branch}): ${error}. Using the cached commit.`,
          ),
        );
      }
    }
  } else {
    if (cached) rmSync(dest, { recursive: true, force: true });
    mkdirSync(dirname(dest), { recursive: true });
    if (!cloneRepo(url, branch, dest)) {
      // The url is reported back to the user (install lists it under `missing`),
      // so strip any token it carries before it reaches a build log.
      throw new Error(redactCredentials(`Failed to clone ${url} (branch: ${branch}) into ${dest}`));
    }
  }
  // A SHA pin already checked out needs no git at all — that keeps a fully
  // pinned project offline after its first clone, which is much of the point.
  if (pin && getRepoCommit(dest) === pin) return;
  if (pin && !checkoutPin(dest, pin)) {
    console.warn(
      redactCredentials(
        `[repo-manager] could not check out pin ${pin} in source clone ${dest}. Using the cached commit.`,
      ),
    );
  }
}

/**
 * Step 2 of {@link ensureRepo}: resolve a `decl.path` source. A bare local path
 * must already exist; a url-backed managed cache dir is ours to populate, so it
 * is cloned in-place on first miss and kept in sync on every later call.
 */
function ensureLocalPathRepo(
  decl: SourceDeclaration,
  reposDir: string,
  opts?: { pin?: string; projectDir?: string },
): string {
  const projectDir = opts?.projectDir ?? resolve(reposDir, "..");
  const resolved = resolve(projectDir, decl.path as string);
  // A managed cache dir whose origin maps to a different key than `decl.url` is a
  // stale flat-slug collision — wipe and re-clone so the wrong repo is never used.
  const exists = existsSync(resolved);
  if (exists && (!decl.url || remoteUrlMatches(resolved, decl.url))) {
    // Only a clone we own may be moved; a user's checkout is read as-is.
    if (decl.url && !decl.factory && hasOwnGit(resolved) && isManagedCacheDir(resolved)) {
      syncManagedClone(resolved, decl.url, decl.branch ?? "main", opts?.pin);
    }
    return resolved;
  }
  if (!decl.url) {
    throw new Error(`Local repository path not found: ${decl.path} (resolved: ${resolved})`);
  }
  // Miss, or a stale flat-slug collision (origin maps to a different key) — the
  // shared helper wipes and re-clones so the wrong repo is never used.
  syncManagedClone(resolved, decl.url, decl.branch ?? "main", opts?.pin);
  return resolved;
}

/**
 * Step 3 of {@link ensureRepo}: clone/pull a remote `decl.url` source into the
 * shared global cache, checkout any pin, then symlink it into the project-local
 * `.skaile/cache/sources/`. Returns the global cache path.
 */
function ensureRemoteRepo(
  decl: SourceDeclaration,
  name: string,
  reposDir: string,
  opts?: { pin?: string; projectDir?: string },
): string {
  const cacheDest = join(getGlobalCacheDir(), name);
  syncManagedClone(cacheDest, decl.url as string, decl.branch ?? "main", opts?.pin);
  linkCacheIntoProject(cacheDest, reposDir, name);
  return cacheDest;
}

/**
 * Create (or repair) the project-local `.skaile/cache/sources/<name>` symlink pointing
 * at the global cache. A stale real directory (legacy clone) is replaced. All
 * symlink failures are swallowed — callers fall back to the cache dir directly.
 */
function linkCacheIntoProject(cacheDest: string, reposDir: string, name: string): void {
  const projectDest = join(reposDir, name);
  const projectExists = existsSync(projectDest);
  const isSymlink = projectExists && lstatSync(projectDest).isSymbolicLink();

  if (!projectExists) {
    mkdirSync(reposDir, { recursive: true });
    try {
      symlinkSync(cacheDest, projectDest);
    } catch {
      // Symlink failed (Windows?), just use cache dir directly
    }
  } else if (!isSymlink) {
    // Stale real directory (legacy clone) — replace with symlink to global cache
    rmSync(projectDest, { recursive: true });
    try {
      symlinkSync(cacheDest, projectDest);
    } catch {
      // Symlink failed, fall through to return cacheDest
    }
  }
}

// ── Scanning ─────────────────────────────────────────────────────────────────

/**
 * Derive the `"<org>/<repo>"` slug from a directory's git `origin` remote.
 * Returns `undefined` when `rootDir` is not a git repo, has no `origin` remote,
 * or the URL is not a parseable GitHub repo. Mirrors the store's
 * `repoSlugFromSourceUrl` so local and remote feeds key the grouping tree
 * identically.
 *
 * @param rootDir - Absolute path to the scanned repository root
 * @returns `"<org>/<repo>"` or `undefined`
 * @docLink packages/core/api-reference#repo-slug-from-git-remote
 */
function repoSlugFromGitRemote(rootDir: string): string | undefined {
  const r = spawnSync("git", ["-C", rootDir, "config", "--get", "remote.origin.url"], {
    encoding: "utf8",
    stdio: "pipe",
    env: GIT_ENV,
  });
  if (r.status !== 0) return undefined;
  const m = /github\.com[/:]([^/]+)\/([^/]+?)(?:\.git)?\/?$/.exec(r.stdout.trim());
  return m ? `${m[1]}/${m[2]}` : undefined;
}

/**
 * Resolve a scanned source's canonical publisher: the declared `publisher` in
 * the root `skaile.manifest.yaml`, else the GitHub org from the `origin` remote.
 * Returns `undefined` when neither is available, so callers can fall back to the
 * repo config key.
 *
 * Single source of truth shared by the source/library scanner (`scanRepo`) and
 * the index/discovery side (`LocalCatalogSource.sync`) so both key an asset
 * identically. The org fallback is lowercased to its GitHub-shaped slug
 * (GitHub orgs are case-insensitive — a cased remote like `Acme` must not split
 * from the lowercase index ref). A declared manifest publisher has its single
 * leading `@` (the scope sigil, added automatically by the ref formatter)
 * stripped so the scan/display refs match the decode path — `@acme` would
 * otherwise render as `@@acme` and be un-addable.
 *
 * @param repoDir - Absolute path to the scanned repository root
 * @returns The publisher slug (no `@` sigil) or `undefined`
 * @docLink packages/core/api-reference#resolve-source-publisher
 */
export function resolveSourcePublisher(repoDir: string): string | undefined {
  const manifestPath = join(repoDir, SKAILE_MANIFEST_FILENAME);
  if (existsSync(manifestPath)) {
    try {
      const raw = parse(readFileSync(manifestPath, "utf8")) as Record<string, unknown> | null;
      if (raw && typeof raw.publisher === "string" && raw.publisher.length > 0)
        return raw.publisher.startsWith("@") ? raw.publisher.slice(1) : raw.publisher;
    } catch {}
  }
  const org = repoSlugFromGitRemote(repoDir)?.split("/")[0];
  return org ? normalizeGithubOrg(org) : undefined;
}

/**
 * Scan a repository directory and return all discovered asset catalog entries.
 * Delegates to `scanDirectory` from `manifest.ts`, then stamps the `repo` slug
 * derived once from the root's git remote onto every entry (the GitHub
 * coordinate is a property of the scanned root, not of individual assets).
 *
 * The `publisher` field is overridden with the source's declared canonical
 * publisher (`skaile.yaml` `publisher` / GitHub org) so refs emitted from
 * filename-convention scans match the provenance index `resolveAll` builds.
 * Without this, a source whose declared publisher differs from its config slug
 * yields refs (`name@<slug>`) that resolution can't find (`name@<publisher>`).
 *
 * @param repoDir - Absolute path to the repository root
 * @param repoName - Repository config key; fallback publisher and `repository` field
 * @returns Array of `CatalogEntry` objects found in the repository
 * @docLink packages/core/api-reference#scan-repo
 */
export async function scanRepo(repoDir: string, repoName: string): Promise<CatalogEntry[]> {
  const repo = repoSlugFromGitRemote(repoDir);
  const publisher = resolveSourcePublisher(repoDir) ?? repoName;
  return (await scanDirectory(repoDir, repoName)).map((e) => ({
    ...e,
    publisher,
    ...(repo ? { repo } : {}),
  }));
}

// ── Resolution ───────────────────────────────────────────────────────────────

/**
 * Resolve a single asset reference to its `CatalogEntry` by scanning declared repositories.
 *
 * Search order:
 * 1. Explicit `@repository` qualifier on the ref — only that repo is searched.
 * 2. `opts.preferRepo` — searched first when set (repo affinity for transitive deps).
 * 3. All declared repos in declaration order.
 *
 * @param ref - Parsed asset reference to resolve
 * @param repositories - Repository declarations from `skaile.yaml`
 * @param reposDir - Project-local source-clone directory (`.skaile/cache/sources/`)
 * @param opts - Optional: `projectDir` for link lookup, `preferRepo` for affinity
 * @returns Matching `CatalogEntry`, or `null` if not found in any repository
 * @docLink packages/core/api-reference#resolve-asset
 */
export async function resolveAsset(
  ref: AssetRef,
  repositories: Record<string, SourceDeclaration>,
  reposDir: string,
  opts?: { projectDir?: string; preferRepo?: string },
): Promise<CatalogEntry | null> {
  let repoNames: string[];
  if (ref.publisher) {
    // Explicit @repo qualifier — only search that repo
    repoNames = [ref.publisher];
  } else if (opts?.preferRepo && opts.preferRepo in repositories) {
    // Repo affinity from parent — search preferred repo first, then others
    const rest = Object.keys(repositories).filter((n) => n !== opts.preferRepo);
    repoNames = [opts.preferRepo, ...rest];
  } else {
    repoNames = Object.keys(repositories);
  }

  for (const repoName of repoNames) {
    const decl = repositories[repoName];
    if (!decl) continue;

    const repoDir = resolveRepoDir(decl, repoName, reposDir, opts?.projectDir);
    if (!repoDir || !existsSync(repoDir)) continue;

    const entries = await scanRepo(repoDir, repoName);
    const match = entries.find((e) => e.kind === ref.kind && e.name === ref.name);
    if (match) return match;
  }

  return null;
}

/** Resolve repo dir without cloning (for lookups). Checks links first. */
function resolveRepoDir(
  decl: SourceDeclaration,
  name: string,
  reposDir: string,
  projectDir?: string,
): string | null {
  // Check links
  if (projectDir) {
    const links = readLinks(projectDir);
    if (links[name]) {
      return existsSync(links[name]) ? links[name] : null;
    }
  }

  if (decl.path) {
    const base = projectDir ?? resolve(reposDir, "..");
    const resolved = resolve(base, decl.path);
    return existsSync(resolved) ? resolved : null;
  }

  // Check project-local symlink or global cache. A global cache dir whose origin
  // maps to a different key than `decl.url` is a stale collision — skip it rather
  // than scan the wrong repo (re-clone happens on the next ensureRepo).
  const projectDest = join(reposDir, name);
  if (existsSync(projectDest)) return projectDest;

  const globalDest = join(getGlobalCacheDir(), name);
  if (existsSync(join(globalDest, ".git"))) {
    if (decl.url && !remoteUrlMatches(globalDest, decl.url)) return null;
    return globalDest;
  }

  return null;
}

/**
 * Hard error raised when two or more candidates for the same canonical ref
 * `<publisher>/<kind>:<name>@<version>` disagree on their content sha256 and no
 * `overrides[]` entry resolves the conflict. The message walks the dep chain
 * back to the user-declared root.
 * @docLink packages/core/api-reference#canonical-ref-conflict-error
 */
export class CanonicalRefConflictError extends Error {
  constructor(
    public ref: string,
    public candidates: ProvenanceCandidate[],
    public depChain: string[],
  ) {
    super(
      [
        `error: divergent sha256 for ${ref}`,
        "",
        "  pulled in via:",
        ...depChain.map((s, i) => `    ${" ".repeat(i * 2)}${s}`),
        "",
        "  candidates:",
        ...candidates.map(
          (c) => `    ${c.sourceUrl} @ ${c.commit.slice(0, 8)}\n      sha256: ${c.sha256}`,
        ),
        "",
        "resolve by:",
        "  1. removing one source from skaile.yaml,",
        "  2. configuring a store and using its canonical digest, or",
        "  3. adding to overrides: (with a non-empty reason:)",
      ].join("\n"),
    );
    this.name = "CanonicalRefConflictError";
  }
}

/**
 * Result of a full transitive dependency resolution via `resolveAll`.
 * @docLink packages/core/api-reference#resolve-result
 */
export interface ResolveResult {
  /** Resolved candidates in leaf-first order. */
  resolved: ProvenanceCandidate[];
  /** Refs that no source / store could provide. */
  missing: string[];
  /** "<publisher>/<kind>:<name>" → parent ref ("direct" if user-declared). */
  resolvedBy: Map<string, string>;
  /** Overrides that were applied (lock-side bookkeeping); canonical refs. */
  overridesApplied: Set<string>;
}

/** Minimal store fetcher contract used by the resolver for the catalog branch. */
export interface StoreFetcher {
  getInstallManifest(
    storeUrl: string,
    ref: string,
  ): Promise<{
    sourceUrl: string;
    commit: string;
    sha256: string;
    files: Array<{ path: string; sha256: string }>;
  } | null>;
  getCanonicalDigest(storeUrl: string, ref: string): Promise<{ sha256: string } | null>;
  /**
   * Enumerate the versions a store can serve for a version-less ref
   * (`kind:@<publisher>/name`). Optional — when present, the resolver uses it to
   * discover candidate versions for a store-only asset whose dep ref is bare or
   * ranged (no source clone to derive a version hint from). Absent ⇒ the
   * resolver falls back to the source-derived hint only (legacy behaviour).
   */
  listVersions?(storeUrl: string, ref: string): Promise<string[]>;
}

/** Options for {@link resolveAll}. */
export interface ResolveOpts {
  /** Source-side candidate index built by the walker. */
  provenanceIndex: ProvenanceIndex;
  /** Conflict-resolution overrides (with required reason). */
  overrides: OverrideEntry[];
  /** Trusted store catalogs. */
  stores: StoreEntry[];
  /** Lazy store fetcher; only used when `stores[]` is non-empty. */
  storeFetcher?: StoreFetcher;
  projectDir?: string;
}

const SHA_PIN_RE = /^[0-9a-f]{40}$/i;

/**
 * Ensure a transitive dep ref carries a publisher, inheriting `fallback` (the
 * parent candidate's publisher) when absent. Keeps any `#pin` suffix in place.
 * A ref that already names a publisher is returned unchanged; a bare
 * `kind:name[#pin]` ref is re-emitted in the scope form `kind:@publisher/name[#pin]`.
 */
function qualifyPublisher(ref: string, fallback: string): string {
  const hashIdx = ref.indexOf("#");
  const head = hashIdx === -1 ? ref : ref.slice(0, hashIdx);
  // A scoped ref carries `@publisher/`; bare refs (`kind:name`) don't. `parseAssetRef`
  // throws on a bare ref, so detect the scope sigil by string before building one.
  if (head.includes("@")) return ref;
  const pin = hashIdx === -1 ? undefined : ref.slice(hashIdx + 1) || undefined;
  const colonIdx = head.indexOf(":");
  const kind = colonIdx === -1 ? "skill" : head.slice(0, colonIdx);
  const name = colonIdx === -1 ? head : head.slice(colonIdx + 1);
  return assetRefToStr({ kind, name, publisher: fallback, pin });
}

/**
 * Resolve all dependencies against a content-hash-verified candidate set drawn
 * from every source clone and (optionally) every store.
 *
 * Per dep ref: gather source candidates, optionally extend with store
 * candidates, filter by the SemVer/SHA pin, pick the highest matching version,
 * then detect conflicts — divergent sha256 for the same
 * `(publisher, kind, name, version)` is a hard {@link CanonicalRefConflictError}
 * unless an `overrides[]` entry pins a source. A cheap `getCanonicalDigest`
 * cross-check runs when both a source and a store produced a candidate at the
 * chosen version.
 *
 * @param deps - Top-level canonical asset ref strings (`kind:@<publisher>/name[#pin]`).
 * @param opts - Provenance index, overrides, stores, and an optional store fetcher.
 * @returns `ResolveResult` with resolved candidates, missing refs, provenance map, applied overrides.
 * @docLink packages/core/api-reference#resolve-all
 */
export async function resolveAll(deps: string[], opts: ResolveOpts): Promise<ResolveResult> {
  const resolved: ProvenanceCandidate[] = [];
  const seen = new Set<string>(); // <publisher>/<kind>:<name>
  const missing: string[] = [];
  const resolvedBy = new Map<string, string>();
  const overridesApplied = new Set<string>();
  const overridesByRef = new Map(opts.overrides.map((o) => [o.ref, o]));

  async function visit(refStr: string, parent: string, depChain: string[]): Promise<void> {
    const ref = parseAssetRef(refStr); // throws on floating refs etc.
    if (!ref.publisher) throw new Error(`dep "${refStr}" missing publisher`);
    const key = `${ref.publisher}/${ref.kind}:${ref.name}`;
    if (seen.has(key)) return;
    seen.add(key);

    // 1. Source-side candidates.
    const sourceCandidates = opts.provenanceIndex.get(key) ?? [];

    // 2. Optional store-side candidates.
    const storeCandidates = await gatherStoreCandidates(ref, ref.publisher, sourceCandidates, opts);

    const all = [...sourceCandidates, ...storeCandidates];
    if (all.length === 0) {
      missing.push(refStr);
      return;
    }

    // 3. Filter by pin.
    const filtered = all.filter((c) => matchPin(ref.pin, c.version));
    if (filtered.length === 0) {
      missing.push(refStr);
      return;
    }

    // 4. Highest-version finalists.
    const versions = Array.from(new Set(filtered.map((c) => c.version)));
    const best = versions.sort((a, b) => semver.rcompare(coerceVersion(a), coerceVersion(b)))[0]!;
    const finalists = filtered.filter((c) => c.version === best);
    // Canonical ref == lock key == store wire ref: `kind:@publisher/name#version`.
    const canonicalRef = assetRefToStr({ ...ref, pin: best });

    // 5. Conflict detection.
    const chosen = selectChosenCandidate(
      finalists,
      canonicalRef,
      overridesByRef,
      overridesApplied,
      [...depChain, refStr],
    );

    // 6. Cheap cross-check when both source and store produced a candidate.
    await crossCheckStoreDigest(
      chosen,
      finalists,
      canonicalRef,
      best,
      sourceCandidates,
      storeCandidates,
      opts,
      [...depChain, refStr],
    );

    resolved.push(chosen);
    resolvedBy.set(key, parent);

    // Transitive expansion. Bundle candidates carry canonical dep refs; recurse
    // so a bundle pulls its members (and nested bundles, arbitrary depth). The
    // `seen` set above dedupes shared deps and breaks cycles. A bare ref inherits
    // the parent's publisher (same-source assumption).
    for (const depRef of chosen.deps ?? []) {
      await visit(qualifyPublisher(depRef, chosen.publisher), key, [...depChain, refStr]);
    }
  }

  for (const dep of deps) {
    await visit(dep, "direct", []);
  }
  return { resolved, missing, resolvedBy, overridesApplied };
}

/**
 * Step 2 of `resolveAll`'s `visit`: gather store-side candidates for `ref`.
 * For each store, derive a version hint (source-derived, or — for a bare/ranged
 * pin with no source hint and a `listVersions` capable fetcher — store-enumerated)
 * and fetch an install manifest per candidate version. Pure: reads `opts`, no
 * closure state. Returns `[]` when no store / fetcher is configured.
 */
async function gatherStoreCandidates(
  ref: AssetRef,
  publisher: string,
  sourceCandidates: ProvenanceCandidate[],
  opts: ResolveOpts,
): Promise<ProvenanceCandidate[]> {
  const storeCandidates: ProvenanceCandidate[] = [];
  if (opts.stores.length === 0 || !opts.storeFetcher) return storeCandidates;

  const sourceVersions = sourceCandidates.map((c) => c.version);
  const fetcher = opts.storeFetcher;
  for (const store of opts.stores) {
    // Store-only fallback: with no source-derived version hint and a bare or
    // ranged pin, ask the store which versions it has so the dep can resolve
    // instead of landing in `missing` (exact pins/SHAs need no discovery).
    let versionsHint = sourceVersions;
    if (versionsHint.length === 0 && needsStoreVersionDiscovery(ref.pin) && fetcher.listVersions) {
      versionsHint = await fetcher.listVersions(
        store.url,
        assetRefToStr({ ...ref, pin: undefined }),
      );
    }
    const candidateVersions = enumerateVersionsForPin(ref.pin, versionsHint);
    for (const v of candidateVersions) {
      if (!v) continue;
      const m = await fetcher.getInstallManifest(store.url, assetRefToStr({ ...ref, pin: v }));
      if (m) {
        storeCandidates.push({
          publisher,
          kind: ref.kind,
          name: ref.name,
          version: v,
          sourceUrl: m.sourceUrl,
          commit: m.commit,
          files: m.files,
          sha256: m.sha256,
        });
      }
    }
  }
  return storeCandidates;
}

/**
 * Step 5 of `resolveAll`'s `visit`: pick the chosen candidate among the
 * highest-version finalists. Divergent sha256 is a hard conflict unless an
 * `overrides[]` entry pins a source (recorded in `overridesApplied`); otherwise
 * the pick is deterministic by `sourceUrl` ascending.
 */
function selectChosenCandidate(
  finalists: ProvenanceCandidate[],
  canonicalRef: string,
  overridesByRef: Map<string, OverrideEntry>,
  overridesApplied: Set<string>,
  conflictChain: string[],
): ProvenanceCandidate {
  const uniqueShas = Array.from(new Set(finalists.map((c) => c.sha256)));
  if (uniqueShas.length > 1) {
    const override = overridesByRef.get(canonicalRef);
    if (!override) {
      throw new CanonicalRefConflictError(canonicalRef, finalists, conflictChain);
    }
    const pick = finalists.find((c) => c.sourceUrl === override.source);
    if (!pick) {
      throw new Error(
        `override for ${canonicalRef} points at ${override.source}, ` +
          "which is not among the resolved candidates",
      );
    }
    overridesApplied.add(canonicalRef);
    return pick;
  }
  // No conflict — deterministic pick by sourceUrl asc.
  return [...finalists].sort((a, b) => a.sourceUrl.localeCompare(b.sourceUrl))[0]!;
}

/**
 * Step 6 of `resolveAll`'s `visit`: cheap cross-check that runs only when both a
 * source and a store produced a candidate at the chosen `best` version. A store
 * canonical digest that disagrees with `chosen.sha256` is a hard conflict.
 */
async function crossCheckStoreDigest(
  chosen: ProvenanceCandidate,
  finalists: ProvenanceCandidate[],
  canonicalRef: string,
  best: string,
  sourceCandidates: ProvenanceCandidate[],
  storeCandidates: ProvenanceCandidate[],
  opts: ResolveOpts,
  conflictChain: string[],
): Promise<void> {
  if (
    !opts.storeFetcher ||
    opts.stores.length === 0 ||
    !sourceCandidates.some((c) => c.version === best) ||
    !storeCandidates.some((c) => c.version === best)
  ) {
    return;
  }
  for (const store of opts.stores) {
    const d = await opts.storeFetcher.getCanonicalDigest(store.url, canonicalRef);
    if (d && d.sha256 !== chosen.sha256) {
      throw new CanonicalRefConflictError(
        canonicalRef,
        [...finalists, { ...chosen, sourceUrl: store.url, sha256: d.sha256 }],
        conflictChain,
      );
    }
  }
}

/** Coerce a version string to a semver-comparable form (synthetic 0.0.0-sha.X is valid semver). */
export function coerceVersion(v: string): string {
  return semver.valid(v) ? v : (semver.coerce(v)?.version ?? "0.0.0");
}

/** Match a candidate version against a pin (SemVer range, exact, 40-char SHA, or absent). */
export function matchPin(pin: string | undefined, version: string): boolean {
  if (!pin) return true;
  if (SHA_PIN_RE.test(pin)) {
    // SHA pin matches only the synthetic version it produced.
    return version === `0.0.0-sha.${pin.slice(0, 7)}`;
  }
  if (version.startsWith("0.0.0-sha.")) {
    // Synthetic versions satisfy ONLY their exact literal pin.
    return version === pin;
  }
  if (!semver.valid(version)) return version === pin;
  return semver.satisfies(version, pin, { includePrerelease: false });
}

/**
 * Whether the resolver should ask the store to enumerate versions. True for a
 * bare ref (no pin) or a SemVer range; false for an exact-version pin or a SHA
 * pin (those name a single version, so `getInstallManifest` can be queried
 * directly without discovery).
 */
function needsStoreVersionDiscovery(pin: string | undefined): boolean {
  if (!pin) return true;
  if (SHA_PIN_RE.test(pin)) return false;
  return !semver.valid(pin);
}

function enumerateVersionsForPin(pin: string | undefined, sourceVersionsHint: string[]): string[] {
  if (!pin) return sourceVersionsHint.length > 0 ? sourceVersionsHint : [];
  if (semver.valid(pin)) return [pin];
  // For ranges / SHA pins the resolver re-filters against the source hint.
  return sourceVersionsHint;
}

// ── Status ───────────────────────────────────────────────────────────────────

/**
 * Status summary for a repository — local HEAD, remote HEAD, and how far behind.
 * @docLink packages/core/api-reference#repo-status
 */
export interface RepoStatus {
  /** Logical name of the repository. */
  name: string;
  /** Whether the repo is local, remote (cloned), or linked (dev override). */
  kind: "local" | "remote" | "linked";
  /** Current local HEAD commit SHA. */
  localHead: string;
  /** Remote HEAD commit SHA (fetched during status check). */
  remoteHead: string;
  /** Number of commits the local clone is behind the remote. */
  behind: number;
  /** `true` when `behind === 0`. */
  upToDate: boolean;
  /** Absolute path the link points to (linked repos only). */
  linkedTo?: string;
  /** Human-readable error string when the status check failed. */
  error?: string;
}

/**
 * Check whether a repository clone is up to date with its remote.
 * Fetches the last 50 commits to compare local HEAD with `origin/<branch>`.
 *
 * @param decl - Repository declaration from `skaile.yaml`
 * @param name - Logical repository name
 * @param reposDir - Project-local source-clone directory (`.skaile/cache/sources/`)
 * @param projectDir - Optional project root for link lookup
 * @returns `RepoStatus` describing the current state
 * @docLink packages/core/api-reference#check-repo-status
 */
export function checkRepoStatus(
  decl: SourceDeclaration,
  name: string,
  reposDir: string,
  projectDir?: string,
): RepoStatus {
  // Check for linked override
  if (projectDir) {
    const links = readLinks(projectDir);
    if (links[name]) {
      return {
        name,
        kind: "linked",
        localHead: "",
        remoteHead: "",
        behind: 0,
        upToDate: true,
        linkedTo: links[name],
      };
    }
  }

  const base: RepoStatus = {
    name,
    kind: decl.path ? "local" : "remote",
    localHead: "",
    remoteHead: "",
    behind: 0,
    upToDate: true,
  };

  if (decl.path) return base;

  // Check global cache first, then project-local. A global cache dir whose origin
  // maps to a different key than `decl.url` is a stale collision — fall through to
  // the project-local clone rather than reporting status for the wrong repo.
  let dest = join(getGlobalCacheDir(), name);
  const globalOk =
    existsSync(join(dest, ".git")) && (!decl.url || remoteUrlMatches(dest, decl.url));
  if (!globalOk) {
    dest = join(reposDir, name);
  }
  if (!existsSync(join(dest, ".git"))) {
    return { ...base, error: "Not cloned yet" };
  }

  const branch = decl.branch ?? "main";

  const localHead = getRepoCommit(dest);
  if (!localHead) return { ...base, error: "Failed to read local HEAD" };
  base.localHead = localHead;

  const fetchR = spawnSync("git", ["-C", dest, "fetch", "--depth=50", "origin", branch], {
    encoding: "utf8",
    stdio: "pipe",
    env: GIT_ENV,
  });
  if (fetchR.status !== 0) return { ...base, error: "Fetch failed" };

  const remoteR = spawnSync("git", ["-C", dest, "rev-parse", `origin/${branch}`], {
    encoding: "utf8",
    stdio: "pipe",
  });
  if (remoteR.status !== 0) return { ...base, error: "Failed to read remote HEAD" };
  base.remoteHead = remoteR.stdout.trim();

  const countR = spawnSync(
    "git",
    ["-C", dest, "rev-list", "--left-right", "--count", `HEAD...origin/${branch}`],
    { encoding: "utf8", stdio: "pipe" },
  );
  if (countR.status === 0) {
    const parts = countR.stdout.trim().split(/\s+/);
    base.behind = parseInt(parts[1] ?? "0", 10);
  }

  base.upToDate = base.behind === 0;
  return base;
}
